import React, { useCallback, useEffect, useRef, useState } from "react"
import { Alert, Button, Spin } from "antd"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { DEBUG } from "../../../config"

/**
 * PaymentIframe — composant générique d'encaissement via iframe + postMessage.
 *
 * Agnostique de la plateforme : toute la spécificité (URL, noms de messages, mapping
 * requête/réponse) vit dans un descripteur `protocol` (cf. ./protocols/). Ce composant
 * ne connaît que la mécanique commune : monter l'iframe, faire le handshake de hauteur,
 * écouter les réponses, déclencher le paiement (au clic ou automatiquement).
 *
 * GOTCHA général aux iframes postMessage : ne fonctionne pas en localhost (la plupart
 * des fournisseurs filtrent l'origine). Tester en staging.
 *
 * Props :
 * - protocol  : descripteur de protocole (buildSrc, onReady, parseMessage, buildPayMessage,
 *               iframeId, title, sandbox, heightPadding, autoPay, debugWarning).
 * - params    : données opaques renvoyées par le backend, passées au protocole
 *               (buildSrc/buildPayMessage). Pour Nedarim = l'objet params.nedarim.
 * - onSuccess : (value) => void — réponse de paiement réussie (best-effort côté iframe).
 * - onError   : (message) => void — refus/erreur.
 * - onCancel  : () => void — retour au formulaire (optionnel, masqué pendant le paiement).
 */
export default function PaymentIframe({ protocol, params, onSuccess, onError, onCancel }) {

    const iframeRef = useRef(null)
    const [height, setHeight] = useState(0)
    const [ready, setReady] = useState(false)
    const [paying, setPaying] = useState(false)

    const post = useCallback((message) => {
        const win = iframeRef.current?.contentWindow
        if (win && message) {
            win.postMessage(message, "*")
        }
    }, [])

    const pay = useCallback(() => {
        setPaying(true)
        post(protocol.buildPayMessage(params))
    }, [post, protocol, params])

    // Écoute des messages entrants, normalisés par le protocole.
    useEffect(() => {
        const handler = (event) => {
            const result = protocol.parseMessage(event)
            if (!result || result.kind === "ignore") {
                return
            }

            switch (result.kind) {
                case "height": {
                    const padding = protocol.heightPadding || 0
                    setHeight(parseInt(result.height) + padding)
                    if (!ready) {
                        setReady(true)
                        // Plateformes sans bouton parent : payer dès que l'iframe est prête.
                        if (protocol.autoPay) {
                            pay()
                        }
                    }
                    break
                }
                case "success":
                    setPaying(false)
                    onSuccess?.(result.value)
                    break
                case "error":
                    setPaying(false)
                    onError?.(result.message || t`Le paiement a été refusé.`)
                    break
                default:
                    break
            }
        }

        window.addEventListener("message", handler, false)
        return () => window.removeEventListener("message", handler, false)
    }, [protocol, params, onSuccess, onError, ready, pay])

    const handleLoad = () => {
        const messages = protocol.onReady ? protocol.onReady() : []
        for (const m of (messages || [])) {
            post(m)
        }
    }

    const src = protocol.buildSrc(params)

    return (
        <div style={{ display: "flex", justifyContent: "center", paddingBottom: 150 }}>
            <div style={{ padding: "0 10px", width: "100%", maxWidth: 520 }}>

                {DEBUG && protocol.debugWarning && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={t`Mode développement`}
                        description={protocol.debugWarning}
                    />
                )}

                <div style={{ position: "relative", minHeight: 120 }}>
                    {!ready && (
                        <div style={{ textAlign: "center", padding: 40 }}>
                            <Spin />
                        </div>
                    )}
                    <iframe
                        id={protocol.iframeId}
                        ref={iframeRef}
                        title={protocol.title || "payment"}
                        scrolling="no"
                        onLoad={handleLoad}
                        src={src}
                        sandbox={protocol.sandbox || "allow-scripts allow-same-origin allow-forms allow-popups"}
                        style={{
                            width: "100%",
                            height: `${height}px`,
                            border: "none",
                            display: "block",
                            boxSizing: "border-box",
                        }}
                    />
                </div>

                {!protocol.autoPay && (
                    <Button
                        type="primary"
                        block
                        size="large"
                        loading={paying}
                        disabled={!ready}
                        onClick={pay}
                        style={{ marginTop: 16 }}
                    >
                        <Trans>Payer</Trans>
                    </Button>
                )}

                {onCancel && (
                    <Button
                        type="link"
                        block
                        disabled={paying}
                        onClick={onCancel}
                        style={{ marginTop: 8 }}
                    >
                        <Trans>Annuler</Trans>
                    </Button>
                )}
            </div>
        </div>
    )
}
